import React, {useState} from 'react';
import {CompetitionPeriod, useCompetitionList} from "@/src/entities/competition/model/queries";
import Competition from "@/src/features/competition/ui/Competition";
import {Button} from "@heroui/react";
import {CalendarX} from "lucide-react";
import {useTranslations} from "next-intl";

function CompetitionSkeleton() {
    return (
        <div className="flex w-full flex-col sm:flex-row gap-5 rounded-2xl border border-neutral-200 bg-white p-4">
            <div className="h-48 w-full animate-pulse rounded-xl bg-neutral-100 sm:h-40 sm:w-44 sm:min-w-44" />
            <div className="flex flex-1 flex-col gap-3 py-1">
                <div className="h-6 w-2/3 animate-pulse rounded bg-neutral-100" />
                <div className="h-4 w-1/2 animate-pulse rounded bg-neutral-100" />
                <div className="h-4 w-full animate-pulse rounded bg-neutral-100" />
                <div className="h-4 w-4/5 animate-pulse rounded bg-neutral-100" />
            </div>
        </div>
    );
}

function CompetitionList({path, period = 'active'}: {path: string, period?: CompetitionPeriod}) {
    const t = useTranslations("competitions");
    const [page, setPage] = useState(1);
    const {data: competitionList, isLoading} = useCompetitionList(period, page)

    if (isLoading) {
        return (
            <div className="w-full flex flex-col gap-4">
                {[0, 1, 2].map((i) => <CompetitionSkeleton key={i} />)}
            </div>
        );
    }

    const competitions = competitionList?.results ?? [];

    if (competitions.length === 0) {
        return (
            <div className="flex w-full flex-col items-center gap-2 rounded-2xl border border-dashed border-neutral-300 bg-white px-6 py-14 text-center">
                <CalendarX size={32} className="text-neutral-300" />
                <div className="text-lg font-bold text-neutral-700">
                    {period === 'past' ? t("empty.pastTitle") : t("empty.activeTitle")}
                </div>
                <div className="text-neutral-500">
                    {period === 'past'
                        ? t("empty.pastText")
                        : t("empty.activeText")}
                </div>
            </div>
        );
    }

    return (
        <div className="w-full flex flex-col gap-4">
            {competitions.map((competition) => (
                <Competition competition={competition} key={competition.id} path={path} />
            ))}
            {(competitionList?.total_pages ?? 1) > 1 && (
                <div className="mt-2 flex items-center justify-center gap-3">
                    <Button
                        variant="flat"
                        isDisabled={!competitionList?.previous_number}
                        onPress={() => setPage(competitionList?.previous_number ?? page)}
                    >
                        {t("pagination.previous")}
                    </Button>
                    <span className="text-sm font-medium text-neutral-500">
                        {t("pagination.pageOf", {page: competitionList?.current_number ?? 1, total: competitionList?.total_pages ?? 1})}
                    </span>
                    <Button
                        variant="flat"
                        isDisabled={!competitionList?.next_number}
                        onPress={() => setPage(competitionList?.next_number ?? page)}
                    >
                        {t("pagination.next")}
                    </Button>
                </div>
            )}
        </div>
    );
}

export default CompetitionList;
